import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState, type FormEvent } from 'react'
import { devicesTableQuery, saveManyDeviceSettings, selectionQuery, today, type Period } from '../api/securitee'
import { ApiError } from '../api/client'
import { Content } from '../ui/Shell'
import { DataTable, useTableState } from '../ui/DataTable'
import { Spinner } from '../ui/Spinner'
import { toast } from '../ui/notify'
import { deviceColumns } from './columns'
import { BlockText, EMAIL_BLOCK_DOCUMENTS_TEXT, EMAIL_BLOCK_EXECUTABLES, EMAIL_SCANNING_LIMITS, EXTENSION_PROTECTION_TEXT, SecuriteeDeviceTabs, USB_LIMITS, USB_READ_ONLY, WEB_UPLOADS_LIMITS } from './DeviceTabs'
import type { ExtraOption } from './DeviceTabs'
import { SettingsRadio } from '../ui/SettingsParts'

/**
 * Securitee::all_devices - `#allDevices` cu perioada din `SecuriteeAllDevices`
 * (azi, pana la alegerea din calendar; de la ea atarna coloanele Files /
 * Files size si filele randului), widgetul statiilor bifate
 * (`devicesWidget.php`: "Devices dashboard" si "Device settings"), si randul
 * desfacut cu filele din `getActionTabs`.
 */

export const TABLE_ID = 'allDevices'

/**
 * PERIOADA DIN ANTET: DOUA `<input type="date">`
 * -----------------------------------------------------------------------------
 *
 * Filtrul asta e antetul comun al listelor din Securitee, Printeeng,
 * Saveenergee si Productivitee (`productivitee/period.tsx`,
 * `ManagementPage`, fila Evolution).
 *
 * Avea `DatePicker`, si cu el cele doua neajunsuri masurate pe 24 septembrie
 * 2026 (Edge headless, foile adevarate, 1600x900): casuta iesea 204x34 - mai
 * lata decat cautarea de langa, de 200px - iar iconita dinauntru nu se desena
 * deloc, fiindca `fa-calendar-o` e un nume din Font Awesome 4 si `app.css`
 * poarta Font Awesome 5, unde `:before`-ul lui are `content: none`. Ramanea
 * vizibila doar iconita de pe randul de deasupra, cea de AFARA casutelor.
 *
 * Casuta nativa e ingusta (131x30 masurati), isi deseneaza singura calendarul
 * inauntru, si e deja controlul de data al restului aplicatiei - de la
 * `ui/EventsChrome` pana la `inventoree/EndpointPerformancePage`. Iconita
 * separata dinainte iese: fiecare casuta o are acum pe a ei, in interior.
 *
 * `value` a fost si ramane `yyyy-mm-dd`, deci catre server nu se schimba nimic.
 * O schimbare in GOL se lasa deoparte: casuta veche era `readOnly` si n-avea
 * cum sa fie golita, iar o perioada fara capete ar cere lista pe nimic.
 */
export function PeriodFilter({ period, onChange }: { period: Period; onChange(p: Period): void }) {
  return (
    <div className="daterange tk-header-extra" style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
      {/* `width: auto` peste `form-control`, care e `display: block; width: 100%`
          in Bootstrap 3 si, intr-un rand flex, intinde casuta cat tine bara. */}
      <input
        className="form-control input-sm"
        type="date"
        id="filterFrom"
        name="from"
        aria-label="From"
        style={{ width: 'auto' }}
        value={period.from}
        onChange={(e) => {
          if (e.target.value !== '') onChange({ ...period, from: e.target.value })
        }}
      />
      <span>-</span>
      <input
        className="form-control input-sm"
        type="date"
        id="filterTo"
        name="to"
        aria-label="To"
        style={{ width: 'auto' }}
        value={period.to}
        onChange={(e) => {
          if (e.target.value !== '') onChange({ ...period, to: e.target.value })
        }}
      />
    </div>
  )
}

/** devices/multiple_select/devicesWidget.php + devicesDashboard.php + devicesSettings.php */
function SelectedDevicesWidget({ chosen, shown, period, onHide, onSaved }: { chosen: number[]; shown: boolean; period: Period; onHide(): void; onSaved(): void }) {
  const [tab, setTab] = useState<'dashboard' | 'settings'>('dashboard')
  const open = shown && chosen.length > 0
  return (
    <div className="multiple_check_widget widget" style={{ display: shown ? undefined : 'none' }}>
      <div className="widget-header bg-module">
        <div className="widget-icon"><i className="fas fa-shield-alt"></i></div>
        <h2 className="widget-title">Selected Devices Options</h2>
        <div className="widget-action">
          <button className="btn hide-widget" type="button" onClick={onHide}><i className="fas fa-times"></i></button>
        </div>
      </div>
      <div className="widget-content">
        <div style={{ width: '20%', display: 'inline-block', boxSizing: 'border-box' }}>
          <ul className="nav nav-metro-tabs tabs-left">
            <li data-class-nav="nav-content" className={tab === 'dashboard' ? 'active' : undefined}>
              <a href="#" onClick={(e) => { e.preventDefault(); setTab('dashboard') }}>Devices dashboard</a>
            </li>
            <li data-class-nav="nav-content" className={tab === 'settings' ? 'active' : undefined}>
              <a href="#" onClick={(e) => { e.preventDefault(); setTab('settings') }}>Device settings</a>
            </li>
          </ul>
        </div>
        <div className="nav-content" style={{ display: 'inline-block', width: '79%', boxSizing: 'border-box', verticalAlign: 'top', paddingLeft: 15 }}>
          {open && tab === 'dashboard' ? <SelectionDashboard chosen={chosen} period={period} /> : null}
          {open && tab === 'settings' ? <SelectionSettings chosen={chosen} onSaved={onSaved} /> : null}
        </div>
      </div>
    </div>
  )
}

/* Cifrele sunt pe PERIOADA aleasa pe ecran, aceeasi pe care o folosesc lista si
   filele randului desfacut. Pana pe 25 septembrie 2026 casetele astea arata tot
   istoricul masinilor bifate, socotit rand cu rand in memoria serverului. */
function SelectionDashboard({ chosen, period }: { chosen: number[]; period: Period }) {
  const q = useQuery(selectionQuery(chosen, period))
  if (q.error) return <div className="alert alert-danger">Something went wrong: {q.error.message}</div>
  if (!q.data) return <Spinner />
  const d = q.data
  const stat = (color: string, icon: string, number: string, desc: string, more: string, cols: number) => (
    <div className={`col-${cols}`}>
      <div className={`dashboard-stat ${color}`}>
        <div className="visual"><i className={icon}></i></div>
        <div className="details"><div className="number">{number}</div><div className="desc">{desc}</div></div>
        <div className="more">{more}</div>
      </div>
    </div>
  )
  return (
    <>
      <header className="panel-heading sm" data-color="theme-inverse"><h4><strong>Devices</strong> dashboard</h4></header>
      <div className="panel-body">
        {stat('red-intense', 'fa fa-unlock-alt', `${d.protected_devices} out of ${d.all_devices}`, 'Protected Devices', 'Antivirus', 3)}
        {stat('blue-madison', 'fa fa-folder', String(d.files), 'Total Files', d.files_size, 2)}
        {stat('blue-madison', 'fa fa-file', String(d.documents), 'Documents', d.documents_size, 2)}
      </div>
    </>
  )
}

const MULTI: { name: string; label: string; block: number; extra?: ExtraOption; blockText?: string; limits?: string }[] = [
  { name: 'block_usb', label: 'USB', block: 2, extra: USB_READ_ONLY, limits: USB_LIMITS },
  { name: 'email_scanning', label: 'Email Scanning', block: 1, extra: EMAIL_BLOCK_EXECUTABLES, blockText: EMAIL_BLOCK_DOCUMENTS_TEXT, limits: EMAIL_SCANNING_LIMITS },
  { name: 'web_uploads', label: 'Web Uploads', block: 1, limits: WEB_UPLOADS_LIMITS },
  { name: 'public_shares', label: 'Public Shares', block: 1 },
  { name: 'admin_shares', label: 'Admin Shares', block: 1 },
  { name: 'cryptolocker', label: 'CryptoLocker Protection', block: 1, blockText: EXTENSION_PROTECTION_TEXT },
]

function SelectionSettings({ chosen, onSaved }: { chosen: number[]; onSaved(): void }) {
  const [busy, setBusy] = useState(false)
  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (busy) return
    const data = new FormData(e.currentTarget)
    const form: Record<string, string> = {}
    for (const m of MULTI) form[m.name] = String(data.get(m.name) ?? '0')
    setBusy(true)
    try {
      const body = await saveManyDeviceSettings(chosen, form)
      toast(body.message, 'Success', 'success')
      onSaved()
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Something went wrong.', 'Error', 'error')
    } finally {
      setBusy(false)
    }
  }
  const cell = { textAlign: 'center' as const, background: '#b3b3b3' }
  return (
    <>
      <header className="panel-heading sm" data-color="theme-inverse"><h4><strong>Device</strong> settings</h4></header>
      <div className="panel-body">
        <form method="post" action="/securitee/saveMultipleDeviceSettings" id="devices_settings" className="form-horizontal" onSubmit={onSubmit}>
          {chosen.map((d) => <input key={d} type="hidden" name="devices[]" value={d} />)}
          <table className="table table-hover">
            <thead>
              <tr>
                <th className="a-left" style={{ width: 200 }}>Option</th>
                <th className="a-left" style={{ width: 200, textAlign: 'center' }}>Active</th>
                <th className="a-left" style={{ width: 200, textAlign: 'center' }}>Block</th>
                <th className="a-left" style={{ textAlign: 'center' }}></th>
              </tr>
            </thead>
            <tbody>
              {MULTI.map((m) => (
                <tr key={m.name}>
                  <td className="a-left">
                    <strong>{m.label}</strong>
                    {m.blockText ? <BlockText text={m.blockText} /> : null}
                    {m.limits ? <div className="securitee-usb-limits" style={{ fontSize: 11, color: '#7f8c8d', fontWeight: 400, marginTop: 4 }}>{m.limits}</div> : null}
                  </td>
                  <td style={cell} className="a-left"><SettingsRadio aria-label={`${m.label}: active`} className="radio_blue" name={m.name} value={0} defaultChecked /></td>
                  <td style={cell} className="a-left">
                    <SettingsRadio aria-label={`${m.label}: block`} className="radio_red" name={m.name} value={m.block} />
                  </td>
                  <td style={{ textAlign: 'center' }} className="a-left">
                    {m.extra ? (
                      <div style={{ margin: '0 auto', width: 150 }}>
                        <SettingsRadio className="securitee_settings" name={m.name} value={m.extra.value} label={m.extra.label} />
                      </div>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <input type="submit" className="metro-btn green medium save_device_settings" value="Save" disabled={busy} />
        </form>
      </div>
    </>
  )
}

export function SecuriteeDevicesPage() {
  const client = useQueryClient()
  const [period, setPeriod] = useState<Period>(() => ({ from: today(), to: today() }))
  const [state, setState] = useTableState(TABLE_ID, { start: 0, length: 25, order: { column: 2, dir: 'asc' }, search: '' })
  const [selected, setSelected] = useState<Set<number>>(() => new Set())
  const [widgetHidden, setWidgetHidden] = useState(false)
  const [gravityZone, setGravityZone] = useState(false)
  const columns = useMemo(() => deviceColumns(gravityZone), [gravityZone])
  const orderColumn = columns.find((c) => c.index === state.order.column) ?? columns[2]!
  const query = useQuery({
    ...devicesTableQuery({ draw: 1, start: state.start, length: state.length, orderColumn: orderColumn.index, orderName: orderColumn.name ?? '', orderDir: state.order.dir, search: state.search, ...period }),
    placeholderData: keepPreviousData,
  })
  /* Coloana GravityZone apare numai cand serverul spune ca firma e legata. */
  const linked = query.data?.gravityzone === true
  if (query.data && linked !== gravityZone) setGravityZone(linked)
  const rows = query.data?.data ?? []

  const onTableClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement
    if (target instanceof HTMLInputElement && target.classList.contains('oneDevice')) {
      const idNum = Number(target.value)
      setSelected((s) => {
        const next = new Set(s)
        if (target.checked) next.add(idNum)
        else next.delete(idNum)
        return next
      })
      setWidgetHidden(false)
    } else if (target instanceof HTMLInputElement && target.classList.contains('allDevices')) {
      setSelected(target.checked ? new Set(rows.map((r) => r.ID)) : new Set())
      setWidgetHidden(false)
    }
  }

  return (
    <Content>
      <div id="content" onClick={onTableClick}>
        <DataTable
          id={TABLE_ID}
          columns={columns}
          rows={rows.map((r) => ({ ...r, __checked: selected.has(r.ID) }))}
          total={query.data?.recordsTotal ?? 0}
          fetching={query.isFetching}
          error={query.isError ? (query.error as Error).message : null}
          onRetry={() => void query.refetch()}
          state={state}
          onState={setState}
          rowId={(r) => String(r.DT_RowId)}
          headerButtons={<PeriodFilter period={period} onChange={setPeriod} />}
          details={(row) => <SecuriteeDeviceTabs id={Number(row.ID)} period={period} />}
        >
          <SelectedDevicesWidget
            chosen={[...selected]}
            shown={selected.size > 0 && !widgetHidden}
            period={period}
            onHide={() => setWidgetHidden(true)}
            onSaved={() => {
              setSelected(new Set())
              void client.invalidateQueries({ queryKey: ['securitee'] })
            }}
          />
        </DataTable>
      </div>
    </Content>
  )
}
